import {
  TGetAvailablePrizesForRedemptionResponse,
  TGetTreatPrizeWinsResponse,
} from '@daymade/api-contracts';
import { PrizeWinStatus } from '@daymade/common-lib/dbEnums';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { tryApi } from 'utils/api/common';
import { handleNonOkResponse } from 'utils/api/response';
import { drawApiClient, winnerApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';

type TTreatWins = (TGetTreatPrizeWinsResponse[number] & {
  prizeOptions?: TGetAvailablePrizesForRedemptionResponse;
})[];

export const unclaimedPrizeWinStatuses: TTreatWins[number]['status'][] = [
  PrizeWinStatus.PENDING,
  PrizeWinStatus.AWAITING_RESELECTION,
];

export const useTreatWins = () => {
  const { currentUser } = useUserStore();
  const router = useRouter();
  const uiStore = useUIStore();
  const [allPwDetails, setAllPwDetails] = useState<TTreatWins>([]);

  const [idxOfSelectedPw, setIdxOfSelectedPw] = useState<number | null>(null);
  const [detailsForSelectedPw, setDetailsForSelectedPw] = useState<
    TTreatWins[number] | null
  >(null);
  const [prizeOptionsForSelectedPw, setPrizeOptionsForSelectedPw] = useState<
    TGetAvailablePrizesForRedemptionResponse | undefined
  >(undefined);
  const [selectedPwHasBeenChosen, setSelectedPwHasBeenChosen] = useState(false);
  const [finishedChoosingPrizes, setFinishedChoosingPrizes] =
    useState<boolean>(false);

  const isLoggedIn = !!currentUser;
  useEffect(() => {
    (async () => {
      if (!router.isReady) return;
      if (!isLoggedIn) {
        return;
      }

      let drawId = parseInt(router.query.draw as string);
      if (!drawId) {
        const res = await tryApi(
          drawApiClient.public.getLatestCompletedWeeklyDrawId(),
        );

        if (res.status === HttpStatusCode.OK) {
          drawId = res.body;
        }
      }

      const resp = await tryApi(
        winnerApiClient.auth.getTreatPrizeWins({
          query: {
            drawId,
          },
        }),
      );

      if (resp.status === HttpStatusCode.OK) {
        const prizeWins = resp.body;

        if (prizeWins.length > 0) {
          setAllPwDetails(prizeWins);
          const idxOfFirstPendingPrize = prizeWins.findIndex((pw) =>
            unclaimedPrizeWinStatuses.includes(pw.status),
          );
          setIdxOfSelectedPw(
            idxOfFirstPendingPrize >= 0 ? idxOfFirstPendingPrize : null,
          );
          setFinishedChoosingPrizes(
            prizeWins.every(
              (pw) => !unclaimedPrizeWinStatuses.includes(pw.status),
            )
              ? true
              : false,
          );
        } else {
          router.push('/my-wins');
        }
      } else {
        handleNonOkResponse(resp, uiStore);
      }
    })();
  }, [router.isReady, isLoggedIn]);

  useEffect(() => {
    if (allPwDetails.length === 0 || detailsForSelectedPw === null) return;

    (async () => {
      const idx = allPwDetails.findIndex(
        (pw) => pw.id === detailsForSelectedPw.id,
      );

      // Fetch the redemption options for this win
      let prizeOptions = allPwDetails[idx]?.prizeOptions;
      if (!prizeOptions) {
        const resp = await tryApi(
          winnerApiClient.auth.getAvailablePrizesForRedemption({
            query: {
              prizeWinId: allPwDetails[idx].id,
            },
          }),
        );

        if (resp.status === HttpStatusCode.OK) {
          prizeOptions = resp.body;
          const newTreatWinDetails = [...allPwDetails];
          newTreatWinDetails[idx] = {
            ...newTreatWinDetails[idx],
            prizeOptions,
          };
          setAllPwDetails(newTreatWinDetails);
        } else {
          handleNonOkResponse(resp, uiStore);
        }
      }

      if (!prizeOptions) return;
      setPrizeOptionsForSelectedPw(prizeOptions);
    })();
  }, [detailsForSelectedPw]);

  useEffect(() => {
    setSelectedPwHasBeenChosen(
      detailsForSelectedPw
        ? !unclaimedPrizeWinStatuses.includes(detailsForSelectedPw.status)
        : false,
    );
  }, [detailsForSelectedPw]);

  useEffect(() => {
    setDetailsForSelectedPw(
      idxOfSelectedPw === null
        ? null
        : allPwDetails[idxOfSelectedPw]
          ? allPwDetails[idxOfSelectedPw]
          : null,
    );
  }, [idxOfSelectedPw]);

  useEffect(() => {
    if (finishedChoosingPrizes) {
      setIdxOfSelectedPw(null);
    }
  }, [finishedChoosingPrizes]);

  return {
    allPwDetails,
    setAllPwDetails,
    idxOfSelectedPw,
    setIdxOfSelectedPw,
    detailsForSelectedPw,
    prizeOptionsForSelectedPw,
    selectedPwHasBeenChosen,
    finishedChoosingPrizes,
    setFinishedChoosingPrizes,
  };
};
